<template>
  <div class="flow-configure-setting">
    <template v-if="records && records.length">
      <div class="setting-grid-wrapper">
        <div class="wrapper-main">
          <DataGrid height="auto"
                    size="mini"
                    show-overflow="title"
                    resizable
                    highlight-hover-row
                    :row-class-name="rowClassName"
                    :data="records">
            <DataGridColumn
              type="seq"
              title="序号"
              align="center"
              width="60">
            </DataGridColumn>
            <DataGridColumn
              field="name"
              title="流程名称"
              min-width="120">
            </DataGridColumn>
            <DataGridColumn
              field="version"
              title="版本"
              align="center"
              width="80">
              <template v-slot="{ row }">
                <el-tag size="mini">第 {{ row.version }} 版</el-tag>
              </template>
            </DataGridColumn>
            <DataGridColumn
              field="description"
              title="描述"
              min-width="160">
              <template v-slot="{ row }">
                {{ row.description || '-' }}
              </template>
            </DataGridColumn>
            <DataGridColumn
              field="suspended"
              title="状态"
              align="center"
              width="80">
              <template v-slot="{ row }">
                <span :class="row.suspended ? 'status-suspended' : 'status-active'">
                  {{ row.suspended ? '已停用' : '启用中' }}
                </span>
              </template>
            </DataGridColumn>
            <DataGridColumn
              title="操作"
              align="center"
              width="120"
              fixed="right">
              <template v-slot="{ row }">
                <el-button type="text"
                           size="mini"
                           @click="handleUpdateStatus(row)">
                  {{ row.suspended ? '启用' : '停用' }}
                </el-button>
                <el-divider direction="vertical"></el-divider>
                <el-button type="text"
                           size="mini"
                           class="btn-danger"
                           @click="handleRemoveItem(row)">
                  删除
                </el-button>
              </template>
            </DataGridColumn>
            <span slot="empty">暂无流程历史记录</span>
          </DataGrid>
        </div>
      </div>
      <div class="setting-footer">
        <span class="footer-stat">共 {{ records.length }} 条记录</span>
        <span class="footer-actions">
          <el-tooltip placement="top">
            <template slot="content">
              <i class="el-icon-warning"></i>
              <span>版本迁移服务在一些情形下存在问题，非必须请勿使用！</span>
            </template>
            <el-button type="text"
                       size="small"
                       icon="el-icon-connection"
                       @click="handleMigration">
              版本审批迁移
            </el-button>
          </el-tooltip>
          <el-divider direction="vertical"></el-divider>
          <el-button type="text"
                     size="small"
                     icon="el-icon-delete"
                     @click="handleRemoveAll">
            清除全部流程
          </el-button>
        </span>
      </div>
    </template>
    <template v-else>
      <Hint type="empty">
        <div>流程记录数据为空</div>
      </Hint>
    </template>

    <!-- 删除单条记录确认弹窗 -->
    <DataGridDialog title="删除确认"
                    :visible.sync="removeItemDialog.visible"
                    width="400px"
                    custom-class="flow-configure-setting-dialog">
      <div class="confirm-dialog-content">
        <div class="confirm-message">
          <i class="el-icon-warning"></i>
          <span>确定要删除该流程记录吗？</span>
        </div>
        <div class="confirm-option">
          <el-checkbox v-model="removeItemDialog.forceDelete">
            强制删除
          </el-checkbox>
          <p class="hint-text">强制删除将会忽略业务中的数据异常</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="removeItemDialog.visible = false">取消</el-button>
        <el-button type="danger" size="small" @click="confirmRemoveItem">确定删除</el-button>
      </div>
    </DataGridDialog>

    <!-- 清除全部流程确认弹窗 -->
    <DataGridDialog title="清除确认"
                    :visible.sync="removeAllDialog.visible"
                    width="400px"
                    custom-class="flow-configure-setting-dialog">
      <div class="confirm-dialog-content">
        <div class="confirm-message">
          <i class="el-icon-warning"></i>
          <span>确定要清除所有流程记录吗？</span>
        </div>
        <div class="confirm-option">
          <el-checkbox v-model="removeAllDialog.forceDelete">
            强制删除
          </el-checkbox>
          <p class="hint-text">强制删除将会忽略业务中的数据异常</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="removeAllDialog.visible = false">取消</el-button>
        <el-button type="danger" size="small" @click="confirmRemoveAll">确定清除</el-button>
      </div>
    </DataGridDialog>

    <!-- 启用/停用确认弹窗 -->
    <DataGridDialog :title="updateStatusDialog.targetSuspended ? '停用确认' : '启用确认'"
                    :visible.sync="updateStatusDialog.visible"
                    width="450px"
                    custom-class="flow-configure-setting-dialog">
      <div class="confirm-dialog-content">
        <div class="confirm-message">
          <i class="el-icon-warning"></i>
          <span>确定要{{ updateStatusDialog.targetSuspended ? '停用' : '启用' }}该流程版本吗？</span>
        </div>
        <div class="confirm-option">
          <el-checkbox v-model="updateStatusDialog.cascadeInstances">
            同时{{ updateStatusDialog.targetSuspended ? '停用' : '启用' }}该版本下的所有流程实例
          </el-checkbox>
          <p class="hint-text">
            {{ updateStatusDialog.targetSuspended ? '勾选后，该版本下所有正在运行的流程实例会被挂起' : '勾选后，该版本下所有被挂起的流程实例会恢复运行' }}
          </p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="updateStatusDialog.visible = false">取消</el-button>
        <el-button type="primary"
                   size="small"
                   :loading="updateStatusDialog.submitting"
                   @click="confirmUpdateStatus">确定</el-button>
      </div>
    </DataGridDialog>

    <!-- 版本审批迁移弹窗 -->
    <DataGridDialog :title="migrationDialog.step === 1 ? '版本审批迁移 - 选择版本' : '版本审批迁移 - 确认迁移'"
                    :visible.sync="migrationDialog.visible"
                    :width="migrationDialog.step === 1 ? '450px' : '760px'"
                    custom-class="flow-configure-migration-dialog"
                    :close-on-click-modal="false">
      <!-- 第一步：选择版本 -->
      <div v-if="migrationDialog.step === 1" class="migration-step-1">
        <el-form label-width="80px" size="small">
          <el-form-item label="原始版本">
            <el-select v-model="migrationDialog.sourceVersion"
                       placeholder="请选择原始版本"
                       style="width: 100%">
              <el-option v-for="record in records"
                         :key="record.id"
                         :label="`第 ${record.version} 版 - ${record.name}`"
                         :value="record.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="目标版本">
            <el-select v-model="migrationDialog.targetVersion"
                       placeholder="请选择目标版本"
                       style="width: 100%">
              <el-option v-for="record in records"
                         :key="record.id"
                         :label="`第 ${record.version} 版 - ${record.name}`"
                         :value="record.id"
                         :disabled="record.id === migrationDialog.sourceVersion">
              </el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <!-- 第二步：预览迁移信息 -->
      <div v-if="migrationDialog.step === 2"
           v-loading="migrationDialog.loading"
           class="migration-step-2">
        <!-- 版本信息展示 -->
        <div v-if="migrationDialog.sourceVersionInfo && migrationDialog.targetVersionInfo" class="version-info-section">
          <div class="version-info-row">
            <div class="version-info-item">
              <span class="version-label">原始版本：</span>
              <span class="version-name">{{ migrationDialog.sourceVersionInfo.name }}</span>
              <el-tag size="mini">第 {{ migrationDialog.sourceVersionInfo.version }} 版</el-tag>
            </div>
            <div class="version-arrow">
              <i class="el-icon-right"></i>
            </div>
            <div class="version-info-item">
              <span class="version-label">目标版本：</span>
              <span class="version-name">{{ migrationDialog.targetVersionInfo.name }}</span>
              <el-tag size="mini" type="success">第 {{ migrationDialog.targetVersionInfo.version }} 版</el-tag>
            </div>
          </div>
        </div>

        <!-- 迁移进度展示 -->
        <template v-if="migrationDialog.migrating">
          <div class="migration-progress-section">
            <div class="progress-header">
              <span class="progress-title">迁移进度</span>
              <span class="progress-stats">
              已完成 {{ migrationDialog.migratedProcessInstances + migrationDialog.failedProcessInstances }} / {{ migrationDialog.totalProcessInstances }}
            </span>
            </div>
            <el-progress :text-inside="true" :stroke-width="16"
                         :percentage="migrationDialog.progressPercent"
                         :status="migrationDialog.failedProcessInstances > 0 ? 'warning' : undefined">
            </el-progress>
            <div class="progress-detail">
              <span class="detail-item success">成功：{{ migrationDialog.migratedProcessInstances }}</span>
              <span class="detail-item danger">失败：{{ migrationDialog.failedProcessInstances }}</span>
              <span class="detail-item info">等待：{{ migrationDialog.waitingProcessInstances }}</span>
            </div>
          </div>

          <!-- 迁移详情展示 -->
          <div class="migration-details-section">
            <el-tabs v-model="migrationDialog.activeTab" type="border-card">
              <!-- 全部 Tab -->
              <el-tab-pane :label="`全部 (${migrationDialog.allDetails.length})`" name="all" lazy>
                <DataGrid height="260px"
                          size="mini"
                          border="inner"
                          show-overflow="title"
                          :data="migrationDialog.allDetails">
                  <DataGridColumn field="processInstanceId"
                                  title="流程实例 ID"
                                  min-width="200">
                  </DataGridColumn>
                  <DataGridColumn field="status"
                                  title="状态"
                                  align="center"
                                  width="100">
                    <template v-slot="{ row }">
                      <el-tag v-if="row.status === 'completed'" type="success" size="mini">已完成</el-tag>
                      <el-tag v-else-if="row.status === 'waiting'" type="info" size="mini">等待中</el-tag>
                      <el-tag v-else type="warning" size="mini">{{ row.status }}</el-tag>
                    </template>
                  </DataGridColumn>
                  <DataGridColumn field="result"
                                  title="结果"
                                  align="center"
                                  width="100">
                    <template v-slot="{ row }">
                      <span v-if="row.result === 'success'" class="result-success">成功</span>
                      <span v-else-if="row.result === 'failure'" class="result-failure">失败</span>
                      <span v-else>{{ row.result || '-' }}</span>
                    </template>
                  </DataGridColumn>
                  <DataGridColumn field="migrationMessage"
                                  title="迁移消息"
                                  min-width="200">
                    <template v-slot="{ row }">
                      {{ row.migrationMessage || '-' }}
                    </template>
                  </DataGridColumn>
                  <span slot="empty">暂无数据</span>
                </DataGrid>
              </el-tab-pane>

              <!-- 等待 Tab -->
              <el-tab-pane :label="`等待 (${waitingDetails.length})`" name="waiting" lazy>
                <DataGrid height="260px"
                          size="mini"
                          border="inner"
                          show-overflow="title"
                          :data="waitingDetails">
                  <DataGridColumn field="processInstanceId"
                                  title="流程实例 ID"
                                  min-width="200">
                  </DataGridColumn>
                  <DataGridColumn field="status"
                                  title="状态"
                                  align="center"
                                  width="100">
                    <template v-slot="{ row }">
                      <el-tag type="info" size="mini">等待中</el-tag>
                    </template>
                  </DataGridColumn>
                  <span slot="empty">暂无等待的实例</span>
                </DataGrid>
              </el-tab-pane>

              <!-- 成功 Tab -->
              <el-tab-pane :label="`成功 (${successfulDetails.length})`" name="success" lazy>
                <DataGrid height="260px"
                          size="mini"
                          border="inner"
                          show-overflow="title"
                          :data="successfulDetails">
                  <DataGridColumn field="processInstanceId"
                                  title="流程实例 ID"
                                  min-width="200">
                  </DataGridColumn>
                  <DataGridColumn field="status"
                                  title="状态"
                                  align="center"
                                  width="100">
                    <template v-slot="{ row }">
                      <el-tag type="success" size="mini">已完成</el-tag>
                    </template>
                  </DataGridColumn>
                  <span slot="empty">暂无成功的实例</span>
                </DataGrid>
              </el-tab-pane>

              <!-- 失败 Tab -->
              <el-tab-pane :label="`失败 (${failedDetails.length})`" name="failed" lazy>
                <DataGrid height="260px"
                          size="mini"
                          border="inner"
                          show-overflow="title"
                          :data="failedDetails">
                  <DataGridColumn field="processInstanceId"
                                  title="流程实例 ID"
                                  min-width="200">
                  </DataGridColumn>
                  <DataGridColumn field="status"
                                  title="状态"
                                  align="center"
                                  width="100">
                    <template v-slot="{ row }">
                      <el-tag type="danger" size="mini">已完成</el-tag>
                    </template>
                  </DataGridColumn>
                  <DataGridColumn field="migrationMessage"
                                  title="失败原因"
                                  min-width="250">
                    <template v-slot="{ row }">
                      <span class="error-message">{{ row.migrationMessage || '未知错误' }}</span>
                    </template>
                  </DataGridColumn>
                  <span slot="empty">暂无失败的实例</span>
                </DataGrid>
              </el-tab-pane>
            </el-tabs>
          </div>
        </template>
        <template v-else>
          <div class="migration-info">
            <el-alert type="warning"
                      :closable="false"
                      show-icon>
              <template slot="title">
                共有 {{ migrationDialog.instanceCount }} 个流程实例可以迁移
              </template>
            </el-alert>
          </div>
          <div class="mapping-section">
            <div class="section-header">
              <span class="section-title">节点映射关系</span>
              <span class="section-hint">（节点ID相同的已自动关联，可手动修改）</span>
            </div>
            <DataGrid height="260px"
                      size="mini"
                      :data="migrationDialog.activityMapping">
              <DataGridColumn title="原始版本节点"
                              min-width="200">
                <template v-slot="{ row }">
                  {{ row.sourceActivityName }} [{{ row.sourceActivityId }}]
                </template>
              </DataGridColumn>
              <DataGridColumn title=""
                              align="center"
                              width="60">
                <template slot="header">
                  <i class="el-icon-right mapping-arrow"></i>
                </template>
                <template>
                  <i class="el-icon-right mapping-arrow"></i>
                </template>
              </DataGridColumn>
              <DataGridColumn title="目标版本节点"
                              min-width="200">
                <template v-slot="{ row }">
                  <el-select v-model="row.targetActivityId"
                             placeholder="请选择目标节点"
                             size="mini"
                             clearable
                             style="width: 100%"
                             @change="handleMappingChange(row)">
                    <el-option v-for="activity in targetActivities"
                               :key="activity.id"
                               :label="`${activity.name} [${activity.id}]`"
                               :value="activity.id">
                    </el-option>
                  </el-select>
                </template>
              </DataGridColumn>
              <span slot="empty">暂无节点信息</span>
            </DataGrid>
          </div>
        </template>
      </div>

      <div slot="footer" class="dialog-footer">
        <template v-if="migrationDialog.step === 1">
          <el-button size="small" @click="migrationDialog.visible = false">取消</el-button>
          <el-button type="primary"
                     size="small"
                     :disabled="!migrationDialog.sourceVersion || !migrationDialog.targetVersion"
                     @click="handleNextStep">下一步</el-button>
        </template>
        <template v-if="migrationDialog.step === 2">
          <el-button v-if="!migrationDialog.migrating"
                     size="small"
                     @click="handlePrevStep">上一步</el-button>
          <template v-if="migrationDialog.completed">
            <el-button size="small"
                       @click="migrationDialog.visible = false">关闭窗口</el-button>
          </template>
          <template v-else>
            <el-button type="primary"
                       size="small"
                       :loading="migrationDialog.submitting || migrationDialog.migrating"
                       @click="confirmMigration">确认迁移</el-button>
          </template>
        </template>
      </div>
    </DataGridDialog>
  </div>
</template>

<script>
import DataGrid from '../../../../elements/DataGrid'
import DataGridColumn from '../../../../elements/DataGridColumn'
import DataGridDialog from '../../../../elements/grid/DataGridDialog'
import Hint from '../../../../elements/Hint'

export default {
  name: 'FlowConfigureSetting',
  components: {
    DataGrid,
    DataGridColumn,
    DataGridDialog,
    Hint
  },
  props: {
    settingInfo: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      removeItemDialog: {
        visible: false,
        forceDelete: false,
        recordId: null
      },
      removeAllDialog: {
        visible: false,
        forceDelete: false
      },
      updateStatusDialog: {
        visible: false,
        row: null,
        targetSuspended: false,
        cascadeInstances: false,
        submitting: false
      },
      migrationDialog: {
        visible: false,
        step: 1, // 1: 选择版本, 2: 预览迁移信息
        sourceVersion: null,
        targetVersion: null,
        sourceVersionInfo: null, // 原始版本信息
        targetVersionInfo: null, // 目标版本信息
        instanceCount: 0,
        loading: false,
        submitting: false,
        // 迁移进度信息
        migrating: false,
        // 迁移是否已完成
        completed: false,
        progressPercent: 0,
        totalProcessInstances: 0,
        migratedProcessInstances: 0,
        failedProcessInstances: 0,
        waitingProcessInstances: 0,
        pollingTimer: null, // 轮询定时器
        // 迁移详情数据
        allDetails: [], // 所有实例的完整详情
        successfulIds: [], // 成功实例的 ID 列表
        failedIds: [], // 失败实例的 ID 列表
        waitingIds: [], // 等待实例的 ID 列表
        activeTab: 'all' // 当前激活的 Tab
      }
    }
  },
  computed: {
    records () {
      return this.settingInfo.records || []
    },
    currentRecordId () {
      // 获取第一个未停用的记录ID
      const activeRecord = this.records.find(record => {
        return !record.suspended
      })
      return activeRecord ? activeRecord.id : null
    },
    // 目标版本的所有节点列表
    targetActivities () {
      if (this.migrationDialog.targetVersionInfo && this.migrationDialog.targetVersionInfo.activities) {
        return this.migrationDialog.targetVersionInfo.activities
      }
      return []
    },
    // 根据 ID 筛选成功的实例详情
    successfulDetails () {
      const allDetails = this.migrationDialog.allDetails || []
      const successfulIds = this.migrationDialog.successfulIds || []
      return allDetails.filter(detail => {
        return successfulIds.includes(detail.processInstanceId)
      })
    },
    // 根据 ID 筛选失败的实例详情
    failedDetails () {
      const allDetails = this.migrationDialog.allDetails || []
      const failedIds = this.migrationDialog.failedIds || []
      return allDetails.filter(detail => {
        return failedIds.includes(detail.processInstanceId)
      })
    },
    // 根据 ID 筛选等待的实例详情
    waitingDetails () {
      const allDetails = this.migrationDialog.allDetails || []
      const waitingIds = this.migrationDialog.waitingIds || []
      return allDetails.filter(detail => {
        return waitingIds.includes(detail.processInstanceId)
      })
    }
  },
  watch: {
    'migrationDialog.visible' (newVal) {
      if (!newVal) {
        // 弹窗关闭时清理定时器
        this.stopPollingMigrationStatus()
      }
    }
  },
  methods: {
    rowClassName ({ row }) {
      if (row.id === this.currentRecordId) {
        return 'current-row'
      }
      return ''
    },
    handleUpdateStatus (row) {
      this.updateStatusDialog.row = row
      this.updateStatusDialog.targetSuspended = !row.suspended
      this.updateStatusDialog.cascadeInstances = false
      this.updateStatusDialog.submitting = false
      this.updateStatusDialog.visible = true
    },
    confirmUpdateStatus () {
      const { row, targetSuspended, cascadeInstances } = this.updateStatusDialog
      this.updateStatusDialog.submitting = true
      this.$ajax({
        url: '/workspace/manager/flow/configure/setting/updateDefinitionStatus',
        method: 'post',
        data: this.$qs.stringify({
          flowKey: this.settingInfo.flowKey,
          definitionId: row.id,
          suspended: targetSuspended,
          cascadeInstances: cascadeInstances
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.$message.success(response.data['message'] || (targetSuspended ? '已停用' : '已启用'))
              this.updateStatusDialog.visible = false
              // 通知父组件刷新记录
              this.$emit('status-changed')
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }).catch(() => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.updateStatusDialog.submitting = false
      })
    },
    handleRemoveItem (row) {
      this.removeItemDialog.recordId = row.id
      this.removeItemDialog.forceDelete = false
      this.removeItemDialog.visible = true
    },
    confirmRemoveItem () {
      this.$emit('remove-item', {
        recordId: this.removeItemDialog.recordId,
        forceDelete: this.removeItemDialog.forceDelete
      })
      this.removeItemDialog.visible = false
    },
    handleRemoveAll () {
      this.removeAllDialog.forceDelete = false
      this.removeAllDialog.visible = true
    },
    confirmRemoveAll () {
      this.$emit('remove-all', {
        forceDelete: this.removeAllDialog.forceDelete
      })
      this.removeAllDialog.visible = false
    },
    handleMigration () {
      if (this.records.length < 2) {
        this.$message.warning('至少需要两个版本才能进行迁移')
        return
      }
      // 重置所有迁移相关状态
      this.migrationDialog.visible = true
      this.migrationDialog.step = 1
      this.migrationDialog.sourceVersion = null
      this.migrationDialog.targetVersion = null
      this.migrationDialog.sourceVersionInfo = null
      this.migrationDialog.targetVersionInfo = null
      this.migrationDialog.instanceCount = 0
      this.migrationDialog.activityMapping = []
      // 重置迁移进度状态
      this.migrationDialog.migrating = false
      this.migrationDialog.completed = false
      this.migrationDialog.submitting = false
      this.migrationDialog.progressPercent = 0
      this.migrationDialog.totalProcessInstances = 0
      this.migrationDialog.migratedProcessInstances = 0
      this.migrationDialog.failedProcessInstances = 0
      this.migrationDialog.waitingProcessInstances = 0
      // 重置详情数据
      this.migrationDialog.allDetails = []
      this.migrationDialog.successfulIds = []
      this.migrationDialog.failedIds = []
      this.migrationDialog.waitingIds = []
      this.migrationDialog.activeTab = 'all'
    },
    handleNextStep () {
      // 校验原始版本和目标版本不能相同
      if (this.migrationDialog.sourceVersion === this.migrationDialog.targetVersion) {
        this.$message.warning('原始版本和目标版本不能相同')
        return
      }
      // 重置迁移状态（确保进入第二步时状态是初始的）
      this.migrationDialog.migrating = false
      this.migrationDialog.completed = false
      this.migrationDialog.submitting = false
      this.migrationDialog.progressPercent = 0
      this.migrationDialog.totalProcessInstances = 0
      this.migrationDialog.migratedProcessInstances = 0
      this.migrationDialog.failedProcessInstances = 0
      this.migrationDialog.waitingProcessInstances = 0
      this.migrationDialog.allDetails = []
      this.migrationDialog.successfulIds = []
      this.migrationDialog.failedIds = []
      this.migrationDialog.waitingIds = []

      this.migrationDialog.loading = true
      this.migrationDialog.step = 2

      // 调用后端接口获取迁移预览信息
      const flowKey = this.settingInfo.flowKey
      this.$ajax({
        url: '/workspace/manager/flow/configure/setting/migration/preview',
        method: 'post',
        data: this.$qs.stringify({
          flowKey: flowKey,
          sourceDefinitionId: this.migrationDialog.sourceVersion,
          targetDefinitionId: this.migrationDialog.targetVersion
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              const result = response.data.result || {}
              this.migrationDialog.instanceCount = result.instanceCount || 0
              this.migrationDialog.sourceVersionInfo = result.sourceVersionInfo || null
              this.migrationDialog.targetVersionInfo = result.targetVersionInfo || null

              // 前端组织映射关系
              this.buildActivityMapping()
              break
            }
            default: {
              this.$message.error(response.data['message'] || '获取迁移预览信息失败')
              this.migrationDialog.step = 1
              break
            }
          }
        }
      }).catch(() => {
        this.$message.error('获取迁移预览信息失败')
        this.migrationDialog.step = 1
      }).finally(() => {
        this.migrationDialog.loading = false
      })
    },
    /**
     * 前端组织节点映射关系
     * 规则：节点ID相同的自动关联
     */
    buildActivityMapping () {
      const sourceActivities = (this.migrationDialog.sourceVersionInfo && this.migrationDialog.sourceVersionInfo.activities) || []
      const targetActivities = (this.migrationDialog.targetVersionInfo && this.migrationDialog.targetVersionInfo.activities) || []

      // 构建目标节点的Map，方便查找
      const targetMap = {}
      targetActivities.forEach(activity => {
        targetMap[activity.id] = activity
      })

      // 生成映射关系
      const mapping = []
      const self = this
      sourceActivities.forEach(source => {
        // 查找ID相同的目标节点
        const target = targetMap[source.id]
        mapping.push({
          sourceActivityId: source.id,
          sourceActivityName: source.name,
          targetActivityId: target ? target.id : '',
          targetActivityName: target ? target.name : ''
        })
      })

      self.migrationDialog.activityMapping = mapping
    },
    handlePrevStep () {
      this.stopPollingMigrationStatus()
      this.migrationDialog.step = 1
    },
    handleMappingChange (row) {
      // 当用户修改映射关系时，更新目标节点名称
      if (row.targetActivityId) {
        const targetActivity = this.targetActivities.find(activity => {
          return activity.id === row.targetActivityId
        })
        if (targetActivity) {
          row.targetActivityName = targetActivity.name
        }
      } else {
        row.targetActivityName = ''
      }
    },
    confirmMigration () {
      // 校验可迁移的流程实例数量
      if (this.migrationDialog.instanceCount === 0) {
        this.$message.warning('没有可迁移的流程实例')
        return
      }
      this.$confirm('确定要执行版本迁移吗？此操作不可撤销。', '确认迁移', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.migrationDialog.submitting = true
        const flowKey = this.settingInfo.flowKey

        // 调用后端接口执行迁移
        this.$ajax({
          url: '/workspace/manager/flow/configure/setting/migration/execute',
          method: 'post',
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            flowKey: flowKey,
            sourceDefinitionId: this.migrationDialog.sourceVersion,
            targetDefinitionId: this.migrationDialog.targetVersion,
            activityMapping: this.migrationDialog.activityMapping
          }
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                const result = response.data.result || {}
                const batchId = result.batchId

                // 设置迁移中状态
                this.migrationDialog.migrating = true
                this.migrationDialog.completed = false // 重置完成状态
                this.migrationDialog.totalProcessInstances = this.migrationDialog.instanceCount
                this.migrationDialog.migratedProcessInstances = 0
                this.migrationDialog.failedProcessInstances = 0
                this.migrationDialog.waitingProcessInstances = this.migrationDialog.instanceCount
                this.migrationDialog.progressPercent = 0

                // 开始轮询查询迁移状态
                this.pollMigrationStatus(batchId)
                break
              }
              default: {
                this.$message.error(response.data['message'] || '版本迁移启动失败')
                this.migrationDialog.submitting = false
                break
              }
            }
          }
        }).catch(() => {
          this.$message.error('版本迁移启动失败')
          this.migrationDialog.submitting = false
        })
      }).catch(() => {})
    },
    pollMigrationStatus (batchId) {
      const checkBatchStatus = () => {
        this.$ajax({
          url: '/workspace/manager/flow/configure/setting/migration/batchStatus',
          method: 'post',
          data: this.$qs.stringify({
            batchId: batchId
          })
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                const result = response.data.result || {}

                // 更新进度信息
                this.migrationDialog.totalProcessInstances = result.totalProcessInstances || 0
                this.migrationDialog.migratedProcessInstances = result.migratedProcessInstances || 0
                this.migrationDialog.failedProcessInstances = result.failedProcessInstances || 0
                this.migrationDialog.waitingProcessInstances = result.waitingProcessInstances || 0
                this.migrationDialog.progressPercent = result.progressPercent || 0

                // 保存详情数据（用于 Tab 展示）
                this.migrationDialog.allDetails = result.allDetails || []
                this.migrationDialog.successfulIds = result.successfulIds || []
                this.migrationDialog.failedIds = result.failedIds || []
                this.migrationDialog.waitingIds = result.waitingIds || []

                // 检查批处理是否完成
                if (result && result.completed) {
                  // 迁移完成，显示详细结果
                  const migratedInstances = result.migratedProcessInstances || 0
                  const failedInstances = result.failedProcessInstances || 0

                  if (failedInstances > 0) {
                    this.$message.warning(`版本迁移完成：成功 ${migratedInstances} 个，失败 ${failedInstances} 个`)
                  } else {
                    this.$message.success(`版本迁移完成：成功迁移 ${migratedInstances} 个流程实例`)
                  }

                  // 标记迁移已完成
                  this.migrationDialog.completed = true
                  this.stopPollingMigrationStatus()
                  this.migrationDialog.submitting = false
                  this.$emit('migration-success')
                } else {
                  // 继续轮询
                  this.migrationDialog.pollingTimer = setTimeout(checkBatchStatus, 1000)
                }
                break
              }
              default: {
                this.$message.error('查询迁移状态失败：' + (response.data['message'] || '未知错误'))
                this.stopPollingMigrationStatus()
                this.migrationDialog.migrating = false
                this.migrationDialog.submitting = false
                break
              }
            }
          }
        }).catch(() => {
          // 请求失败，继续轮询重试
          this.migrationDialog.pollingTimer = setTimeout(checkBatchStatus, 2000)
        })
      }

      // 开始第一次查询
      checkBatchStatus()
    },
    stopPollingMigrationStatus () {
      if (this.migrationDialog.pollingTimer) {
        clearTimeout(this.migrationDialog.pollingTimer)
        this.migrationDialog.pollingTimer = null
      }
    }
  },
  beforeDestroy () {
    // 组件销毁时清理定时器
    this.stopPollingMigrationStatus()
  }
}
</script>

<style lang="scss">
@use "../../../../../styles/color" as *;

.flow-configure-setting {
  display: flex;
  flex-direction: column;
  height: 100%;

  .setting-grid-wrapper {
    flex: auto;
    position: relative;

    & > .wrapper-main {
      position: absolute;
      inset: 10px;
    }
  }

  .setting-footer {
    flex: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border-top: 1px solid #ebeef5;
    padding: 10px 15px;

    .footer-stat {
      font-size: 12px;
      color: #909399;
    }
  }

  .current-row {
    color: $set-success-color !important;
    background-color: $set-success-lighter-color !important;
  }

  .status-active {
    color: #67c23a;
  }

  .status-suspended {
    color: #909399;
  }

  .btn-danger {
    color: #f56c6c;
    &:hover {
      color: #f78989;
    }
  }
}

.flow-configure-setting-dialog {
  .confirm-dialog-content {
    padding: 10px 20px;

    .confirm-message {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      font-size: 14px;
      color: #606266;

      i {
        font-size: 24px;
        color: #e6a23c;
        margin-right: 12px;
      }
    }

    .confirm-option {
      padding-left: 36px;

      .hint-text {
        margin: 8px 0 0;
        font-size: 12px;
        color: #909399;
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}

.flow-configure-migration-dialog {
  .migration-step-1 {
    padding: 30px 30px 30px 10px;

    .el-form {
      .el-form-item {
        &:last-child {
          margin-bottom: 0;
        }
        .el-form-item__label {
          font-size: 13px;
        }
      }
    }
  }

  .migration-step-2 {
    padding: 15px;
    min-height: 400px;

    .version-info-section {
      margin-bottom: 12px;
      padding: 16px;
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      border-radius: 4px;

      .version-info-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .version-info-item {
        flex: 1;
        display: flex;
        align-items: center;

        .version-label {
          font-size: 12px;
          color: #909399;
          margin-right: 4px;
        }

        .version-name {
          font-size: 13px;
          font-weight: 500;
          color: #303133;
          margin-right: 8px;
        }
      }

      .version-arrow {
        flex: none;
        padding: 0 16px;
        font-size: 16px;
        color: #409eff;
      }
    }

    .migration-info {
      margin-bottom: 12px;
    }

    .migration-progress-section {
      margin-bottom: 12px;
      padding: 12px 16px;
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      border-radius: 4px;

      .progress-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .progress-title {
          font-size: 13px;
          font-weight: 500;
          color: #303133;
        }

        .progress-stats {
          font-size: 12px;
          color: #606266;
        }
      }

      .progress-detail {
        display: flex;
        gap: 16px;
        margin-top: 10px;
        font-size: 12px;

        .detail-item {
          &.success {
            color: #67c23a;
          }
          &.danger {
            color: #f56c6c;
          }
          &.info {
            color: #909399;
          }
        }
      }
    }

    .migration-details-section {
      .el-tabs {
        &__content {
          padding: 0 10px 10px;
        }
      }

      .result-success {
        color: #67c23a;
        font-weight: 500;
      }

      .result-failure {
        color: #f56c6c;
        font-weight: 500;
      }

      .error-message {
        color: #f56c6c;
        font-size: 12px;
      }
    }

    .mapping-section {
      .section-header {
        margin-bottom: 12px;

        .section-title {
          font-size: 14px;
          font-weight: 500;
          color: #303133;
        }

        .section-hint {
          font-size: 12px;
          color: #909399;
          margin-left: 8px;
        }
      }

      .node-cell {
        display: flex;
        flex-direction: column;
        line-height: 1.4;

        .node-name {
          font-size: 13px;
          color: #303133;
        }

        .node-id {
          font-size: 11px;
          color: #909399;
        }
      }

      .mapping-arrow {
        font-size: 16px;
        color: #409eff;
      }

      .select-option-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        .option-name {
          color: #303133;
        }

        .option-id {
          font-size: 11px;
          color: #909399;
        }
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}
</style>
